import { doc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDoc, setDoc } from '../../lib/offlineFirestore';
import { db, functions } from '../../config/firebase';
import { COLLECTIONS } from '../../constants/collections';
import { normalizeBusinessMode, HD_ORCHIDS_BUSINESS_MODES } from '../config/moduleDefinitions';

const TENANTS_COLLECTION = String(COLLECTIONS?.TENANTS || 'tenants');
const cache = new Map();
const CACHE_MS = 3000;

const idValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim().split('/').filter(Boolean).pop() || '';
  if (value?.id) return String(value.id);
  if (value?.path) return String(value.path).split('/').filter(Boolean).pop() || '';
  return '';
};

const configLooksEnabled = (config = {}, moduleKey = '') => {
  if (!config || typeof config !== 'object') return false;
  const status = String(config.status || '').trim().toLowerCase();
  if (config.enabled === false || config.loginEnabled === false || ['disabled','inactive','blocked','suspended'].includes(status)) return false;
  if (config.enabled === true || status === 'active') return true;
  const module = String(config.module || '').trim().toLowerCase();
  const mode = String(config.businessMode || '').trim().toLowerCase();
  return Boolean(
    (moduleKey && module === moduleKey) ||
    (moduleKey === 'hd_orchids' && ['hd_orchids','both','mixed'].includes(mode)) ||
    (moduleKey === 'cold_storage' && ['cold_storage','both','mixed'].includes(mode)) ||
    config.companyId || config.tenantId || config.appName || config.branding || config.webBranding || config.mobileBranding || config.features
  );
};

const normalizeConfig = (data = {}) => {
  const mode = normalizeBusinessMode(data.businessMode || data.businessSuite || data.businessType || data.enabledBusinessMode);
  const modules = data.businessModules || data.suites || {};
  const coldStorage = modules.coldStorage !== undefined ? modules.coldStorage === true : mode !== HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS;
  const hdOrchids = modules.hdOrchids !== undefined ? modules.hdOrchids === true : mode !== HD_ORCHIDS_BUSINESS_MODES.COLD_STORAGE;
  const resolvedMode = coldStorage && hdOrchids
    ? HD_ORCHIDS_BUSINESS_MODES.BOTH
    : hdOrchids
      ? HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS
      : HD_ORCHIDS_BUSINESS_MODES.COLD_STORAGE;
  return {
    mode: resolvedMode,
    coldStorage,
    hdOrchids,
    employeeSyncEnabled: data.employeeSyncEnabled === true || data?.employeeSync?.enabled === true,
    hdOrchidsBranding: data.hdOrchidsBranding || data?.branding?.hdOrchids || {},
    coldStorageDomain: data?.businessDomains?.coldStorage || data?.domain?.coldStorage || '',
    hdOrchidsDomain: data?.businessDomains?.hdOrchids || data?.domain?.hdOrchids || '',
    raw: data,
  };
};

const service = {
  normalizeConfig,

  async getCompanyConfig(tenantId, force = false) {
    const tenant = idValue(tenantId);
    if (!tenant) return normalizeConfig({});
    const cached = cache.get(tenant);
    if (!force && cached && Date.now() - cached.at < CACHE_MS) return cached.value;
    const [tenantSnap, coldConfigSnap, hdConfigSnap, mixedConfigSnap] = await Promise.all([
      getDoc(doc(db, TENANTS_COLLECTION, tenant)),
      getDoc(doc(db, TENANTS_COLLECTION, tenant, 'appConfigs', 'cold_storage')).catch(() => null),
      getDoc(doc(db, TENANTS_COLLECTION, tenant, 'appConfigs', 'hd_orchids')).catch(() => null),
      getDoc(doc(db, TENANTS_COLLECTION, tenant, 'appConfigs', 'mixed')).catch(() => null),
    ]);
    const root = tenantSnap.exists() ? tenantSnap.data() : {};
    const coldConfig = coldConfigSnap?.exists?.() ? coldConfigSnap.data() : {};
    const hdConfig = hdConfigSnap?.exists?.() ? hdConfigSnap.data() : {};
    const mixedConfig = mixedConfigSnap?.exists?.() ? mixedConfigSnap.data() : {};
    const hasExplicitRootSuite = Boolean(
      root?.businessMode || root?.businessSuite || root?.businessType || root?.enabledBusinessMode ||
      root?.businessModules || root?.suites
    );
    let source = root;
    if (!hasExplicitRootSuite) {
      const inferredCold = configLooksEnabled(coldConfig, 'cold_storage');
      const inferredHd = configLooksEnabled(hdConfig, 'hd_orchids');
      const inferredBoth = configLooksEnabled(mixedConfig, 'mixed') || (inferredCold && inferredHd);
      source = {
        ...root,
        businessMode: inferredBoth ? 'both' : inferredHd ? 'hd_orchids' : 'cold_storage',
        businessModules: { coldStorage: inferredBoth || inferredCold || !inferredHd, hdOrchids: inferredBoth || inferredHd },
        businessDomains: {
          ...(root?.businessDomains || {}),
          coldStorage: root?.businessDomains?.coldStorage || coldConfig?.customDomain || '',
          hdOrchids: root?.businessDomains?.hdOrchids || hdConfig?.customDomain || '',
        },
        hdOrchidsBranding: root?.hdOrchidsBranding || hdConfig?.branding || {},
      };
    }
    const value = normalizeConfig(source);
    cache.set(tenant, { at: Date.now(), value });
    return value;
  },

  /**
   * Tenant admins may update shared employee-sync/domain/HD branding settings,
   * but cannot grant themselves HD Orchids or Cold Storage entitlement.
   * Business mode itself is controlled by Super Admin's updateCompanySecure.
   */
  async updateCompanyConfig(tenantId, patch = {}) {
    const tenant = idValue(tenantId);
    if (!tenant) throw new Error('Company is required.');
    const payload = {
      employeeSyncEnabled: patch.employeeSyncEnabled === true,
      businessDomains: {
        coldStorage: String(patch.coldStorageDomain || '').trim(),
        hdOrchids: String(patch.hdOrchidsDomain || '').trim(),
      },
      ...(patch.hdOrchidsBranding ? { hdOrchidsBranding: patch.hdOrchidsBranding } : {}),
      businessSuiteSettingsUpdatedAt: serverTimestamp(),
    };

    // These are tenant-owned non-entitlement settings. Write them directly to
    // Firestore so Orchid/Mixed Settings do not depend on an HTTPS callable or
    // its CORS configuration. Super Admin still exclusively controls the
    // businessMode/businessModules entitlement fields elsewhere.
    await setDoc(doc(db, TENANTS_COLLECTION, tenant), payload, { merge: true });
    cache.delete(tenant);
    return this.getCompanyConfig(tenant, true);
  },

  async initializeHdOrchids(tenantId) {
    const tenant = idValue(tenantId);
    if (!tenant) throw new Error('Company is required.');
    const call = httpsCallable(functions, 'initializeHdOrchidsTenant');
    const response = await call({ tenantId: tenant });
    cache.delete(tenant);
    return response?.data || { success: true };
  },

  clear(tenantId) {
    cache.delete(idValue(tenantId));
  },
};

export default service;
